import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function DonateSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState(() => sessionId
        ? { status: "polling", payment: null }
        : { status: "error", payment: null });

    useEffect(() => {
        if (!sessionId) return;
        const ctx = { attempts: 0, active: true };
        const MAX = 7;
        const INTERVAL = 2000;

        const poll = async () => {
            try {
                const { data } = await api.get(`/donate/status/${sessionId}`);
                if (!ctx.active) return;
                if (data.payment_status === "paid") {
                    setState({ status: "paid", payment: data });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired", payment: data });
                    return;
                }
                ctx.attempts += 1;
                if (ctx.attempts >= MAX) {
                    setState({ status: "timeout", payment: data });
                    return;
                }
                setTimeout(poll, INTERVAL);
            } catch (e) {
                if (!ctx.active) return;
                setState({ status: "error", payment: null });
            }
        };
        poll();
        return () => { ctx.active = false; };
    }, [sessionId]);

    return (
        <PageLayout>
            <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-testid="donate-success-page">
                {state.status === "polling" && (
                    <div data-testid="donate-status-polling">
                        <Loader2 size={56} className="mx-auto animate-spin text-brand-purple" />
                        <h1 className="font-serif text-4xl mt-6">Confirming your gift...</h1>
                        <p className="mt-3 text-stone-600">Hang tight while we finalize your donation.</p>
                    </div>
                )}
                {state.status === "paid" && (
                    <div data-testid="donate-status-paid">
                        <CheckCircle2 size={72} className="mx-auto text-brand-lime" />
                        <h1 className="font-serif text-5xl mt-6">Thank you 🤍</h1>
                        <p className="mt-3 text-lg text-stone-600">
                            Your gift of <span className="font-semibold">${state.payment?.amount?.toFixed(2)} {state.payment?.currency?.toUpperCase()}</span> is making a real difference. Welcome to the village.
                        </p>
                        <Link to="/home" data-testid="donate-success-home" className="inline-block mt-8 px-7 py-3 rounded-full bg-brand-purple text-white font-semibold">Back to Home</Link>
                    </div>
                )}
                {state.status === "expired" && (
                    <div data-testid="donate-status-expired">
                        <XCircle size={56} className="mx-auto text-brand-red" />
                        <h1 className="font-serif text-4xl mt-6">Session expired</h1>
                        <p className="mt-3 text-stone-600">Your checkout session expired before completion. Please try again.</p>
                        <Link to="/donate" className="inline-block mt-8 px-7 py-3 rounded-full bg-brand-purple text-white font-semibold">Donate Again</Link>
                    </div>
                )}
                {(state.status === "timeout" || state.status === "error") && (
                    <div data-testid="donate-status-error">
                        <h1 className="font-serif text-4xl">We&apos;re still processing</h1>
                        <p className="mt-3 text-stone-600">If your payment was successful, you&apos;ll receive a Stripe receipt by email. If you need help, please contact us.</p>
                        <Link to="/home" className="inline-block mt-8 px-7 py-3 rounded-full bg-brand-purple text-white font-semibold">Back to Home</Link>
                    </div>
                )}
            </section>
        </PageLayout>
    );
}
